import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { LoadingSpinner } from '../components'
import { HouseholdSelectionMeta, LoadingSpinnerMeta } from '../data'

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api'
const IIUK_BASE_URL = process.env.REACT_APP_IIUK_BASE_URL || 'https://iiuk.org'
const IIUK_SSO_REDIRECT_URL = `${IIUK_BASE_URL}/Pages/Festivals/ivc_redirect.php?auto=1`

interface HouseholdMember {
    ID: number
    forenames: string
    surname: string
    fullName: string
    token: string
}

interface HouseholdData {
    authenticatedUserId: number
    family: HouseholdMember[]
}

export const HouseholdSelection: React.FC = () => {
    const meta = HouseholdSelectionMeta
    const [householdData, setHouseholdData] = useState<HouseholdData | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [selecting, setSelecting] = useState(false)
    const navigate = useNavigate()

    useEffect(() => {
        fetchHouseholdData()
    }, [])

    const fetchHouseholdData = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/auth/household`, {
                credentials: 'include',
                headers: {
                    'Accept': 'application/json'
                }
            })

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}))

                // If session expired or no data, redirect to SSO
                if (errorData.redirect_to_sso || response.status === 404 || response.status === 401) {
                    window.location.href = IIUK_SSO_REDIRECT_URL
                    return
                }
                throw new Error('Failed to load household data')
            }

            const data = await response.json()
            setHouseholdData(data)
        } catch (err) {
            console.error('Error fetching household data:', err)
            setError(meta.loadError)
        } finally {
            setLoading(false)
        }
    }

    const handleSelectMember = async (token: string) => {
        setSelecting(true)
        setError(null)

        try {
            const response = await fetch(`${API_BASE_URL}/auth/household/select`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ token })
            })

            if (!response.ok) {
                const errorData = await response.json()
                const errorMessage = errorData.error || 'Failed to complete login'
                // Redirect to login page with the error message
                window.location.href = `/?auth=error&message=${encodeURIComponent(errorMessage)}`
                return
            }

            // Login successful, redirect to home
            window.location.href = '/?auth=success'
        } catch (err: any) {
            console.error('Error selecting household member:', err)
            window.location.href = `/?auth=error&message=${encodeURIComponent(err.message || 'Failed to complete login. Please try again.')}`
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-gray-900">
                <LoadingSpinner meta={LoadingSpinnerMeta} />
            </div>
        )
    }

    if (error && !householdData) {
        return (
            <div className="flex flex-col items-center justify-center h-screen bg-gray-50 dark:bg-gray-900 p-4">
                <Card className="p-6 max-w-md w-full">
                    <div className="text-center">
                        <h2 className="text-xl font-semibold text-danger-600 dark:text-danger-400 mb-4">
                            {meta.errorTitle}
                        </h2>
                        <p className="text-gray-700 dark:text-gray-300 mb-6">{error}</p>
                        <Button onClick={() => navigate('/login')}>
                            {meta.returnToLogin}
                        </Button>
                    </div>
                </Card>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-[url(/public/header_tile_gold.png)] bg-tile text-white flex flex-col items-center justify-center p-4">
            <Card className="w-full max-w-2xl p-6 relative">
                <div className="mb-6 text-center">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        {meta.title}
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        {meta.description}
                    </p>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-md">
                        <p className="text-danger-700 dark:text-danger-400 text-sm">{error}</p>
                    </div>
                )}

                <div className="space-y-3">
                    {householdData?.family.map((member) => {
                        const isAuthenticatedUser = member.ID === householdData.authenticatedUserId

                        return (
                            <button
                                key={member.ID}
                                onClick={() => handleSelectMember(member.token)}
                                disabled={selecting}
                                className={`
                                    w-full p-4 rounded-lg border-2 text-start transition-all
                                    ${isAuthenticatedUser
                                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                                    }
                                    ${!selecting && 'hover:border-primary-400 hover:shadow-md'}
                                    ${selecting && 'opacity-50 cursor-not-allowed'}
                                    focus:outline-none focus:ring-2 focus:ring-primary-500
                                `}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-semibold text-gray-900 dark:text-white">
                                            {member.fullName || `${member.forenames} ${member.surname}`.trim()}
                                        </p>
                                    </div>
                                    {isAuthenticatedUser && (
                                        <span className="px-3 py-1 bg-primary-600 text-white text-xs font-semibold rounded-full">
                                            {meta.youBadge}
                                        </span>
                                    )}
                                </div>
                            </button>
                        )
                    })}
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <Button
                        variant="outline"
                        color="neutral"
                        onClick={() => navigate('/login')}
                        disabled={selecting}
                        className="w-full"
                    >
                        {meta.cancel}
                    </Button>
                </div>
            </Card>
        </div>
    )
}
